import { userStateAdapter } from '../react/state/user-state/user-state-adapter';
import { isValidNumber } from './math/number';
import { isDefined } from './variables';

type UserItemValue = {
  designToolPointSizeMultiplier: string;
  designToolTooltipInMenu: string;
  designToolTooltipInMenuLight: string;
  snappingDialogTooltip: string;
  snappingDialogTooltipLight: string;
  designToolAllHeights: string;
  snappingDialogAllHeights: string;
  snappingDialogSettingsShown: string;
  designToolCurveMeasure: string;
  alignerGcpListColumns: string;
};

type UserItemKey = keyof UserItemValue;

function getUserItemKey(key: UserItemKey): string | null {
  const snapshot = userStateAdapter.getSnapshot();
  if (!snapshot.isLoaded) {
    return null;
  }
  const user = snapshot.getActiveUser();
  if (!user?.id) {
    return null;
  }
  return `${user.id}_${key}`;
}

/**
 * Sets an item in the local storage unique to the current user. Nothing is stored before the user
 * is loaded, or where the local storage is blocked or full.
 */
function setUserItem<K extends UserItemKey>(key: K, value: UserItemValue[K]) {
  const storageKey = getUserItemKey(key);
  if (!isDefined(storageKey)) {
    return;
  }

  try {
    localStorage.setItem(storageKey, value);
  } catch {
    // The setting is then only kept for this session, like before the user is loaded.
  }
}

/**
 * Gets an item from the local storage unique for the current user, or the default where there is
 * none, before the user is loaded, or where the local storage is blocked.
 */
function getUserItemOrDefault<K extends UserItemKey>(
  key: K,
  defaultValue: UserItemValue[K],
): UserItemValue[K] {
  const storageKey = getUserItemKey(key);
  if (!isDefined(storageKey)) {
    return defaultValue;
  }

  let item: string | null;
  try {
    item = localStorage.getItem(storageKey);
  } catch {
    // Blocked: the default is then used, as before the user is loaded.
    return defaultValue;
  }
  return (item as UserItemValue[K]) ?? defaultValue; // Return the item or default value if null
}

/**
 * Gets an item from the local storage unique for the current user.
 * If the item stored is not a valid number then the default value will be returned instead.
 */
function getNumericUserItemOrDefault<K extends UserItemKey>(key: K, defaultValue: number): number {
  const value = getUserItemOrDefault(key, defaultValue.toString());

  if (isValidNumber(value)) {
    return Number(value);
  }

  return defaultValue;
}

export const localStorageUtils = {
  setUserItem,
  getNumericUserItemOrDefault,
  getUserItemOrDefault,
};
